import { memo, useEffect, useMemo, useState } from "react";
import { Radio, WifiOff } from "lucide-react";
import { fmtPct, fmtUsd } from "@/lib/api";
import { ago } from "@/components/LiveStamp";
import { FlashPrice } from "@/components/FlashPrice";

const SECONDS_PER_ITEM = 2.6; // scroll speed: one full pass ≈ items × this
const TITLE = {
  live: "Live — feed connected and this asset traded within the last 60 s",
  quiet: "Quiet — feed connected but no trade for over a minute; last trade price",
  polled: "Polled — no real-time pair; CoinGecko quote refreshed every ~20 s",
  stale: "Stale — feed disconnected or no trade for over 5 min; last price received, not a current quote",
  connecting: "Connecting — feed connected, waiting for this asset's first trade",
  snapshot: "Snapshot — CoinGecko list price, refreshed every few minutes",
};

// One coin cell. memo'd on the values it prints so the live flush only touches cells whose price moved.
const Item = memo(function Item({ sym, price, change, state, age, source }) {
  const up = (change ?? 0) >= 0;
  return (
    <div className="apx-ticker-item" data-testid={`ticker-item-${sym}`} data-state={state} data-source={source || undefined} aria-label={`${sym} ${fmtUsd(price)} ${fmtPct(change)} ${state}`} title={`${TITLE[state] || TITLE.stale}${source ? ` · ${source}` : ""}${age ? ` · updated ${age}` : ""}`}>
      <span className="apx-ticker-sym">{sym}</span>
      <span className={`apx-ticker-price ${state === "live" ? "" : "apx-ticker-delayed"}`}><FlashPrice value={price}>{fmtUsd(price)}</FlashPrice></span>
      <span className={`apx-ticker-chg ${change == null ? "text-slate-500" : up ? "text-emerald-400" : "text-red-400"}`}>
        {change == null ? "—" : `${up ? "▲" : "▼"} ${Math.abs(change).toFixed(2)}%`}
      </span>
      {state === "stale" && <span className="apx-ticker-dot" />}
      {state === "quiet" && <span className="apx-ticker-dot" style={{ background: "#fde047" }} />}
      {state === "polled" && <span className="apx-ticker-dot" style={{ background: "#67e8f9" }} />}
    </div>
  );
});

// coins: CoinGecko top list already loaded by the page; liveRef: ref to the live quotes map (same symbols). No extra API calls.
// memo'd on `coins` only — the parent re-renders on live flushes, the ticker re-reads the ref on its own 1 s cadence.
export const PriceTicker = memo(function PriceTicker({ coins = [], liveRef }) {
  const [snap, setSnap] = useState(() => ({ live: liveRef?.current || {}, now: Date.now() }));
  useEffect(() => {
    const t = setInterval(() => setSnap({ live: liveRef?.current || {}, now: Date.now() }), 1000);
    return () => clearInterval(t);
  }, [liveRef]);
  const { live: livePrices, now } = snap;
  const feed = livePrices.__feed || {};

  const rows = useMemo(() => {
    const seen = new Set();
    return coins
      .map((c) => {
        const sym = (c.symbol || "").toUpperCase();
        if (!sym || seen.has(sym)) return null;
        seen.add(sym);
        const q = livePrices[sym];
        const has = q && q.price != null;
        const state = has ? q.state : "snapshot";
        return { sym, price: has ? q.price : c.current_price, change: has && q.change24 != null ? q.change24 : c.price_change_percentage_24h, state, age: has ? ago(q.at, now) : null, source: has ? q.sourceLabel : null };
      })
      .filter(Boolean);
  }, [coins, livePrices, now]);

  if (!rows.length) return null;
  const liveCount = rows.filter((r) => r.state === "live").length;
  const quietCount = rows.filter((r) => r.state === "quiet").length;
  const polledCount = rows.filter((r) => r.state === "polled").length;
  const staleCount = rows.filter((r) => r.state === "stale").length;
  const feedLive = !!feed.live;
  const venues = Object.values(feed.sources || {});
  const venueTxt = venues.length ? venues.map((s, i) => `feed ${i + 1} ${s.live ? "connected" : "down"} (${s.pairs} pairs)`).join(" · ") : "";
  const duration = Math.max(40, Math.round(rows.length * SECONDS_PER_ITEM));

  return (
    <div className="apx-ticker" data-testid="price-ticker" role="marquee" aria-live="off">
      <div className={`apx-ticker-status ${feedLive ? "apx-ticker-status-live" : "apx-ticker-status-stale"}`} data-testid="ticker-status"
           title={feedLive ? `Real-time feeds: ${venueTxt} · last message ${ago(feed.lastMsgAt, now)} · ${liveCount} traded in the last 60 s, ${quietCount} quiet (no trade >1 min), ${staleCount} stale, ${polledCount} polled from CoinGecko (no live pair)` : `Real-time feeds disconnected — prices are the last ones received. ${venueTxt}`}>
        {feedLive ? <Radio className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
        <span>{feedLive ? `FEED ON · ${liveCount}/${rows.length} live` : staleCount ? `FEED DOWN · stale` : "CONNECTING"}</span>
        {feedLive && (quietCount > 0 || polledCount > 0) && <span className="hidden sm:inline text-[9px] opacity-80">{[quietCount && `${quietCount} quiet`, polledCount && `${polledCount} polled`].filter(Boolean).join(" · ")}</span>}
        {!feedLive && <span className="hidden sm:inline text-[9px] opacity-80">{staleCount ? `last tick ${ago(feed.lastMsgAt, now)}` : "waiting for exchange feed"}</span>}
      </div>
      <div className="apx-ticker-viewport">
        <div className="apx-ticker-track" style={{ "--ticker-duration": `${duration}s` }} data-testid="ticker-track">
          {[0, 1].map((copy) => (
            <div className="apx-ticker-list" key={copy} aria-hidden={copy === 1}>
              {rows.map((r) => <Item key={r.sym} {...r} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
